import api from './api';
import axios from 'axios';
import { Persona, PersonaCreate, PersonaUpdate, PersonaMetrics } from '../types/persona';

// Helper function to normalize persona data
function normalizePersona(persona: any): Persona {
  console.log("Normalizing persona:", persona.name, "with data:", persona);
  
  // Log personas with missing or invalid IDs to help identify backend issues
  if (!persona.id) {
    console.error('Backend returned persona without ID:', persona);
  } else if (persona.id === 'undefined' || persona.id === 'null') {
    console.error(`Backend returned persona with invalid ID "${persona.id}":`, persona);
  }
  
  // Handle both allow and allowedTools field names
  const allowedTools = persona.allow || persona.allowedTools || [];
  
  // Ensure capabilities is properly initialized
  const capabilities = persona.capabilities || {
    creativity: 0,
    technical: 0,
    analytical: 0,
    communication: 0
  };
  
  // Check for description fields
  console.log(`Persona ${persona.name} description fields:`, {
    description: persona.description,
    shortDescription: persona.shortDescription,
    fullDescription: persona.fullDescription,
    // Check if description might be in data field
    dataDescription: persona.data?.description,
    // Check if it might be directly in the persona object
    directDescription: typeof persona === 'object' ? Object.keys(persona).filter(key => key.includes('desc')) : []
  });
  
  // Try to extract description from data if it exists
  let description = persona.description;
  let shortDescription = persona.shortDescription;
  
  // If description is in the data field, use it
  if (!description && persona.data && persona.data.description) {
    description = persona.data.description;
    console.log(`Found description in data field for ${persona.name}:`, description);
  }
  
  // If shortDescription is in the data field, use it
  if (!shortDescription && persona.data && persona.data.shortDescription) {
    shortDescription = persona.data.shortDescription;
    console.log(`Found shortDescription in data field for ${persona.name}:`, shortDescription);
  }
  
  return {
    ...persona,
    description: description,
    shortDescription: shortDescription,
    capabilities: capabilities,
    tags: persona.tags || [],
    allowedTools: allowedTools,
    patterns: persona.patterns || [],
    antipatterns: persona.antipatterns || [],
    tethics: persona.tethics || [],
    data: persona.data ?? null,
  };
}

export async function getPersonas(): Promise<Persona[]> {
  try {
    const url = '/personas/';
    console.log('[API-DEBUG] Fetching personas with URL:', url);
    
    const response = await api.get(url);

    // Log raw shape of first forge persona found
    const forgeSample = response.data.find((p: any) =>
      p?.data?.engineering_profile || p?.data?.data?.engineering_profile
    );
    if (forgeSample) {
      console.group('[personaService] raw forge persona sample');
      console.log('name:', forgeSample.name);
      console.log('top-level keys:', Object.keys(forgeSample));
      console.log('data keys:', forgeSample.data ? Object.keys(forgeSample.data) : 'NO DATA KEY');
      console.log('data.engineering_profile:', forgeSample.data?.engineering_profile ?? 'MISS');
      console.log('data.data?.engineering_profile:', forgeSample.data?.data?.engineering_profile ?? 'MISS');
      console.groupEnd();
    } else {
      console.warn('[personaService] no forge persona found in response — all personas lack engineering_profile');
    }

    // Check if any personas are missing IDs
    const missingIds = response.data.filter((p: any) => !p.id || p.id === 'undefined' || p.id === 'null');
    if (missingIds.length > 0) {
      console.error('Backend returned personas with missing or invalid IDs:', missingIds);
    }
    
    return response.data.map(normalizePersona);
  } catch (error) {
    console.error('Error fetching personas:', error);
    
    // Check if the error contains backend:8000
    if (error.message && error.message.includes('backend:8000')) {
      console.error('[API-DEBUG] Detected backend:8000 in error:', error.message);
    }
    
    throw error; // Re-throw to maintain original error handling
  }
}

export async function getPersona(id: string): Promise<Persona> {
  // Check if id is valid
  if (!id || id === 'undefined' || id === 'null') {
    console.error('Invalid persona ID:', id);
    return createDefaultPersona(id);
  }
  
  try {
    const response = await api.get(`/personas/${id}`);
    return normalizePersona(response.data);
  } catch (error) {
    console.error(`Error fetching persona with ID ${id}:`, error);
    return createDefaultPersona(id);
  }
}

// Helper function to create a default persona when API calls fail
function createDefaultPersona(id: string): Persona {
  return {
    id: id || 'unknown',
    name: 'Unknown Persona',
    shortDescription: 'This persona could not be loaded',
    fullDescription: '',
    tags: [],
    primarySpecialization: 'Unknown',
    capabilities: {
      creativity: 0,
      technical: 0,
      analytical: 0,
      communication: 0
    },
    allowedTools: [],
    patterns: [],
    antipatterns: [],
    tethics: [],
    prompt: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
}

export async function createPersona(persona: PersonaCreate): Promise<Persona> {
  const response = await api.post('/personas', persona);
  return response.data;
}

export async function updatePersona(persona: PersonaUpdate): Promise<Persona> {
  const response = await api.put(`/personas/${persona.id}`, persona);
  return response.data;
}

export async function deletePersona(id: string): Promise<void> {
  try {
    // First ensure we have the latest auth status and CSRF token
    await api.get('/auth/me');
    
    // Now make the delete request - the interceptor will add the CSRF token
    await api.delete(`/personas/${id}`, {
      withCredentials: true // Ensure cookies are sent
    });
  } catch (error) {
    console.error(`Error deleting persona ${id}:`, error);
    throw error;
  }
}

export async function getPersonaMetrics(id: string): Promise<PersonaMetrics> {
  // Check if id is valid
  if (!id || id === 'undefined' || id === 'null') {
    console.error('Invalid persona ID for metrics:', id);
    return createDefaultMetrics();
  }
  
  try {
    const response = await api.get(`/personas/${id}/metrics`);
    
    // Ensure all expected fields are present
    const metrics = {
      ...createDefaultMetrics(),
      ...response.data
    };
    
    return metrics;
  } catch (error) {
    // Check if it's a 404 error (endpoint not implemented)
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn(`Persona metrics endpoint not available for ${id}`);
      // Return default metrics object instead of throwing
      return createDefaultMetrics();
    }
    console.error(`Error fetching metrics for persona ${id}:`, error);
    return createDefaultMetrics();
  }
}

export async function getPersonaHistory(id: string): Promise<any[]> {
  // Check if id is valid
  if (!id || id === 'undefined' || id === 'null') {
    console.error('Invalid persona ID for history:', id);
    return [];
  }
  
  try {
    const response = await api.get(`/personas/${id}/history`);
    return response.data;
  } catch (error) {
    // Check if it's a 404 error (endpoint not implemented)
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.warn(`Persona history endpoint not available for ${id}`);
      // Return empty array instead of throwing
      return [];
    }
    console.error(`Error fetching history for persona ${id}:`, error);
    return [];
  }
}

// Helper function to create default metrics
function createDefaultMetrics(): PersonaMetrics {
  return {
    successRate: 0,
    avgResponseTime: '0s',
    usageCount: 0,
    overallRating: 0,
    sessionCount: 0
  };
}

export async function searchPersonas(query: string): Promise<Persona[]> {
  try {
    const response = await api.get('/personas/search', {
      params: { q: query }
    });
    
    // Log the raw data from the backend to help identify issues
    console.log(`Raw search results for query "${query}":`, response.data);
    
    // Check if any personas are missing IDs
    const missingIds = response.data.filter((p: any) => !p.id || p.id === 'undefined' || p.id === 'null');
    if (missingIds.length > 0) {
      console.error('Backend returned search results with missing or invalid IDs:', missingIds);
    }
    
    return response.data.map(normalizePersona);
  } catch (error) {
    console.error(`Error searching personas with query "${query}":`, error);
    throw error; // Re-throw to maintain original error handling
  }
}

export async function importPersonas(
  file: File, 
  options: {
    updateExisting?: boolean;
    skipDuplicates?: boolean;
    matchBy?: 'id' | 'name' | 'both';
  } = {}
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  
  // Add options with defaults
  formData.append('update_existing', String(options.updateExisting ?? true));
  formData.append('skip_duplicates', String(options.skipDuplicates ?? false));
  formData.append('match_by', options.matchBy ?? 'id');
  
  const response = await api.post('/persona-manager/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
}

export async function exportPersonasAsJsonl(personaIds: string[]): Promise<void> {
  try {
    // Log the IDs we're trying to export
    console.log('Exporting personas with IDs:', personaIds);
    
    // Manually construct the query string
    const queryString = personaIds.map(id => `ids=${encodeURIComponent(id)}`).join('&');
    console.log('Export URL:', `/persona-manager/export?${queryString}`);
    
    const response = await api.get(`/persona-manager/export?${queryString}`, {
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `personas_export_${personaIds.length}.jsonl`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error exporting personas:', error);
    throw error;
  }
}

export async function getPersonasByTag(tag: string): Promise<Persona[]> {
  try {
    const response = await api.get(`/personas/tag/${encodeURIComponent(tag)}`);
    return response.data.map(normalizePersona);
  } catch (error) {
    console.error(`Error fetching personas with tag "${tag}":`, error);
    
    // Fallback: filter personas by tag client-side if the endpoint doesn't exist
    try {
      const allPersonas = await getPersonas();
      return allPersonas.filter(persona => 
        persona.tags && persona.tags.includes(tag)
      );
    } catch (fallbackError) {
      console.error('Fallback filtering failed:', fallbackError);
      return [];
    }
  }
}

export async function getPersonaTags(): Promise<{name: string, count: number}[]> {
  try {
    const response = await api.get('/personas/tags');
    return response.data;
  } catch (error) {
    console.error('Error fetching persona tags:', error);
    
    // Fallback: extract tags from all personas if the endpoint doesn't exist
    try {
      const allPersonas = await getPersonas();
      const tagCounts: Record<string, number> = {};
      
      allPersonas.forEach(persona => {
        if (persona.tags) {
          persona.tags.forEach(tag => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        }
      });
      
      return Object.entries(tagCounts).map(([name, count]) => ({ name, count }));
    } catch (fallbackError) {
      console.error('Fallback tag extraction failed:', fallbackError);
      return [];
    }
  }
}
